import { useEffect, useState } from "react";
import { getCredentials } from "@connect/credentials-storage";
import { createFramesClient } from "@connect/data/frames/client";
import { createInternalClient } from "@connect/data/internal/client";
import { alpha3ToAlpha2 } from "@connect/i18n";
import { log } from "@connect/observability";
import { fetchCustomerCountry } from "./fetch-customer-country";

interface UseDefaultResidenceParams {
  apiBaseUrl: string;
  clientHeaders: Record<string, string>;
  apiKey: string;
  enabled: boolean;
}

async function fetchDefaultResidence({
  apiBaseUrl,
  clientHeaders,
  apiKey,
}: Omit<UseDefaultResidenceParams, "enabled">): Promise<string | null> {
  const { customerToken, clientToken } = await getCredentials();

  if (customerToken && clientToken) {
    const framesClient = createFramesClient({
      baseUrl: apiBaseUrl,
      headers: {
        ...clientHeaders,
        "x-client-token": clientToken,
        Authorization: `Bearer ${customerToken}`,
      },
    });

    const savedCountry = await fetchCustomerCountry(framesClient);
    if (savedCountry) {
      return alpha3ToAlpha2(savedCountry);
    }
  }

  if (!apiKey) {
    return null;
  }

  const internalClient = createInternalClient({ baseUrl: apiBaseUrl, headers: clientHeaders });
  const ip = await internalClient
    .GET("/v3/ip_address", { params: { query: { apiKey } } })
    .catch(() => null);

  if (!ip?.data) {
    log.warn("connect.onboarding.ip_country_failed", { status: ip?.response.status ?? 0 });
    return null;
  }

  return ip.data.alpha2 || null;
}

export function useDefaultResidence({
  apiBaseUrl,
  clientHeaders,
  apiKey,
  enabled,
}: UseDefaultResidenceParams): string | null {
  const [country, setCountry] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    let cancelled = false;

    void fetchDefaultResidence({ apiBaseUrl, clientHeaders, apiKey }).then((result) => {
      if (!cancelled) {
        setCountry(result);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, clientHeaders, apiKey, enabled]);

  return country;
}
